import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
// material-ui
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Button,
    FormHelperText,
    Grid,
    TextField,
    useMediaQuery,
    Alert,
    Snackbar,
    Autocomplete,
    IconButton,
    Card,
    CardContent,
    Typography,
    Divider
} from '@mui/material';
// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import useScriptRef from 'hooks/useScriptRef';
import AnimateButton from 'ui-component/extended/AnimateButton';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// assets
import { gridSpacing } from 'store/constant';
import MainCard from 'ui-component/cards/MainCard';
import Loading from 'components/Loading/Loading';
import { getOperatingPerId } from 'services/operating';
import { getMaintenancePlanPerId, getResourceMaintenancePlan, postMaintenancePlan, updateMaintenancePlan, deleteMaintenancePlan } from 'services/maintenancePlan';
import { getTeamTaskPost } from 'services/task';
import 'react-toggle/style.css';
import Toggle from 'react-toggle';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';

// ===========================|| MAINTENANCE PLAN ||=========================== //

const MaintenancePlan = ({ ...others }) => {
    const params = useParams();
    const formRef = useRef(null);
    const formSectionRef = useRef(null);
    
    // Função para formatar data para formato brasileiro
    const formatDateToBRL = (dateString) => {
        if (!dateString) return '';
        
        try {
            // Se a data já está no formato dd/mm/yyyy, retorna como está
            if (dateString.includes('/')) {
                return dateString;
            }
            
            // Para datas no formato yyyy-mm-dd, cria a data corretamente
            const [year, month, day] = dateString.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            
            if (isNaN(date.getTime())) return dateString; // Retorna original se não for data válida
            
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return dateString; // Retorna original em caso de erro
        }
    };
    
    const themeButton = createTheme({
        status: {
            danger: '#e53e3e'
        },
        palette: {
            primary: {
                main: '#0971f1',
                darker: '#053e85'
            },
            neutral: {
                main: '#64748B',
                contrastText: '#fff'
            }
        }
    });
    
    const initialMaintenance = {
        id: '',
        task: null,
        frequency: null,
        equipament: null,
        type_os: null,
        team: null,
        amount: '',
        data_proxima_manutencao: '',
        active: true
    };
    
    const theme = useTheme();
    const navigate = useNavigate();
    const scriptedRef = useScriptRef();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const matchDownXS = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [options, setOptions] = useState({
        task: [],
        frequency: [],
        equipament: [],
        type_os: [],
        team: []
    });
    const [operating, setOperating] = useState('');
    const isDisabled = params.action === 'view' ? true : false;
    const [maintenance, setMaintenance] = useState([]);
    const [currentMaintenance, setCurrentMaintenance] = useState(initialMaintenance);
    const [editingId, setEditingId] = useState(null);
    const idUnit = useSelector((state) => state.user.unit || '');

    useEffect(() => {
        viewPerId();
    }, [params.idOperating]);

    useEffect(() => {
        getOperating();
    }, [params.idOperating]);

    useEffect(() => {
        getResource();
    }, []);

    // Buscar equipes quando o tipo de OS for selecionado
    useEffect(() => {
        if (currentMaintenance.type_os && currentMaintenance.type_os.id) {
            getTeamsByTypeOs(currentMaintenance.type_os.id);
        } else {
            setOptions(prev => ({ ...prev, team: [] }));
        }
    }, [currentMaintenance.type_os]);

    function viewPerId() {
        getMaintenancePlanPerId(params.idOperating).then((resp) => {
            setMaintenance(
                resp.data.map((desc) => ({
                    id: desc.id,
                    task: { id: desc.idTask, label: desc.task },
                    frequency: { id: desc.idFrequency, label: desc.frequency },
                    equipament: { id: desc.idEquipament, label: desc.equipament },
                    type_os: { id: desc.idTypeOs, label: desc.typeOs },
                    team: { id: desc.idTeam, label: desc.team },
                    amount: desc.amount,
                    data_proxima_manutencao: desc.dataProximaManutencao ? desc.dataProximaManutencao.split(' ')[0] : (desc.initialDate ? desc.initialDate.split(' ')[0] : ''),
                    active: desc.active === 1 ? true : false
                }))
            );
        });
    }

    const handleChangeInput = (e) => {
        let name = e.target.name;
        setCurrentMaintenance({ ...currentMaintenance, [name]: e.target.value });
    };

    function getOperating() {
        getOperatingPerId(params.idOperating).then((resp) => setOperating({ id: resp.data.id, label: resp.data.nome, idUnit: resp.data.unit?.id }));
    }

    function getResource() {
        getResourceMaintenancePlan(idUnit).then((resp) => {
            const taskOptions = resp.data?.task || [];
            const frequencyOptions = resp.data?.frequency || [];
            const equipamentOptions = resp.data?.equipament || [];
            const typeOsOptions = resp.data?.type_os || [];
            const teamOptions = resp.data?.team || [];
            
            setOptions({
                task: taskOptions,
                frequency: frequencyOptions,
                equipament: equipamentOptions,
                type_os: typeOsOptions,
                team: teamOptions
            });
            
            // Pré-seleciona automaticamente se houver apenas uma opção
            if (!editingId) {
                setCurrentMaintenance(prev => ({
                    ...prev,
                    task: taskOptions.length === 1 ? taskOptions[0] : prev.task,
                    frequency: frequencyOptions.length === 1 ? frequencyOptions[0] : prev.frequency,
                    equipament: equipamentOptions.length === 1 ? equipamentOptions[0] : prev.equipament,
                    type_os: typeOsOptions.length === 1 ? typeOsOptions[0] : prev.type_os,
                    team: teamOptions.length === 1 ? teamOptions[0] : prev.team
                }));
            }
        }).catch((error) => {
            console.error('Erro ao buscar recursos:', error);
            setOptions({
                task: [],
                frequency: [],
                equipament: [],
                type_os: [],
                team: []
            });
        });
    }

    // Função para buscar equipes baseado no tipo de OS
    const getTeamsByTypeOs = async (typeOsId) => {
        try {
            const response = await getTeamTaskPost(typeOsId, idUnit);
            const teams = response.data || [];
            setOptions(prev => ({ ...prev, team: teams }));
            
            // Se houver apenas uma equipe, pré-seleciona automaticamente
            if (teams.length === 1 && !editingId) {
                setCurrentMaintenance(prev => ({ ...prev, team: teams[0] }));
            }
        } catch (error) {
            console.error('Erro ao buscar equipes:', error);
            setOptions(prev => ({ ...prev, team: [] }));
        }
    };

    const handleSaveMaintenance = async () => {
        // Validação de campos obrigatórios
        if (!currentMaintenance.task || !currentMaintenance.frequency || !currentMaintenance.type_os || !currentMaintenance.amount || !currentMaintenance.data_proxima_manutencao) {
            setError('Por favor, preencha todos os campos obrigatórios (*).');
            return;
        }

        setError('');
        setLoading(true);

        const data = {
            idOperating: parseInt(params.idOperating),
            task: currentMaintenance.task,
            frequency: currentMaintenance.frequency,
            equipament: currentMaintenance.equipament,
            type_os: currentMaintenance.type_os,
            team: currentMaintenance.team,
            amount: currentMaintenance.amount,
            data_proxima_manutencao: currentMaintenance.data_proxima_manutencao,
            active: currentMaintenance.active
        };

        try {
            const resp = await postMaintenancePlan(data);
            setSuccess(resp.data.success);
            setCurrentMaintenance(initialMaintenance);
            setTimeout(() => {
                viewPerId();
                setSuccess('');
            }, 2000);
        } catch (e) {
            setError(e.response?.data?.error || 'Erro ao salvar plano de manutenção.');
            setTimeout(() => {
                setError('');
            }, 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleEditMaintenance = (item) => {
        setEditingId(item.id);
        setCurrentMaintenance({
            id: item.id,
            task: item.task,
            frequency: item.frequency,
            equipament: item.equipament,
            type_os: item.type_os,
            team: item.team,
            amount: item.amount,
            data_proxima_manutencao: item.data_proxima_manutencao,
            active: item.active
        });
        
        // Scroll para o formulário
        setTimeout(() => {
            if (formSectionRef.current) {
                formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleUpdateMaintenance = async () => {
        // Validação de campos obrigatórios
        if (!currentMaintenance.task || !currentMaintenance.frequency || !currentMaintenance.type_os || !currentMaintenance.amount || !currentMaintenance.data_proxima_manutencao) {
            setError('Por favor, preencha todos os campos obrigatórios (*).');
            return;
        }

        setError('');
        setLoading(true);

        const data = {
            idOperating: parseInt(params.idOperating),
            task: currentMaintenance.task,
            frequency: currentMaintenance.frequency,
            equipament: currentMaintenance.equipament,
            type_os: currentMaintenance.type_os,
            team: currentMaintenance.team,
            amount: currentMaintenance.amount,
            data_proxima_manutencao: currentMaintenance.data_proxima_manutencao,
            active: currentMaintenance.active
        };

        try {
            const resp = await updateMaintenancePlan(editingId, data);
            setSuccess(resp.data.success);
            setEditingId(null);
            setCurrentMaintenance(initialMaintenance);
            setTimeout(() => {
                viewPerId();
                setSuccess('');
            }, 2000);
        } catch (e) {
            setError(e.response?.data?.error || 'Erro ao atualizar plano de manutenção.');
            setTimeout(() => {
                setError('');
            }, 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setCurrentMaintenance(initialMaintenance);
    };

    const handleDeleteMaintenance = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este plano de manutenção?')) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const resp = await deleteMaintenancePlan(id);
            setSuccess(resp.data.success);
            setTimeout(() => {
                viewPerId();
                setSuccess('');
            }, 2000);
        } catch (e) {
            setError(e.response?.data?.error || 'Erro ao excluir plano de manutenção.');
            setTimeout(() => {
                setError('');
            }, 3000);
        } finally {
            setLoading(false);
        }
    };

    function renderMaintenance() {
        let maintenanceRender = maintenance || [];
        return maintenanceRender.map((desc) => (
            <Card key={desc.id} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        {/* Primeira linha - Informações principais */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Tarefa
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.task?.label || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Equipamento
                                </Typography>
                            </Box>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 500, 
                                    color: '#333',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.3
                                }}
                                title={desc.equipament?.label || 'Não informado'}
                            >
                                {desc.equipament?.label || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Frequência
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.frequency?.label || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        {/* Segunda linha - Configurações */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Tipo de OS
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.type_os?.label || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Equipe
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.team?.label || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Quantidade
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.amount || 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    Data Próxima Manutenção
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {desc.data_proxima_manutencao ? formatDateToBRL(desc.data_proxima_manutencao) : 'Não informado'}
                            </Typography>
                        </Grid>
                        
                        {/* Terceira linha - Status e Ações */}
                        <Grid item xs={12}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mt: 1,
                                pt: 2,
                                borderTop: '1px solid #f0f0f0'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Toggle disabled={true} checked={desc.active} sx={{ mr: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                        {desc.active ? 'Tarefa Ativa' : 'Tarefa Inativa'}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {!isDisabled && (
                                        <IconButton 
                                            aria-label="editar" 
                                            onClick={() => handleEditMaintenance(desc)}
                                            sx={{ 
                                                color: '#1976d2',
                                                '&:hover': { 
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1565c0'
                                                }
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                    <IconButton 
                                        aria-label="remover" 
                                        onClick={() => handleDeleteMaintenance(desc.id)}
                                        disabled={isDisabled}
                                        sx={{ 
                                            color: '#d32f2f',
                                            '&:hover': { 
                                                backgroundColor: '#ffebee',
                                                color: '#c62828'
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        ));
    }

    return (
        <>
            <Formik
                initialValues={{
                    submit: null
                }}
                enableReinitialize
                validationSchema={Yup.object().shape({
                    // Validações podem ser adicionadas aqui
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    // Não usado mais, mas mantido para compatibilidade
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
                    <>
                        {loading && (
                            <Grid container alignItems="center" justifyContent="center">
                                <MainCard style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Loading color="#015641" type="cubes" />
                                </MainCard>
                            </Grid>
                        )}
                        <MainCard spacing={gridSpacing} style={{ padding: 15, margin: 25 }}>
                            <Grid xs={12} md={12} sm={12} container>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        font: 'normal normal bold 35px/44px Myriad Pro',
                                        letterSpacing: '0px',
                                        color: '#015641',
                                        opacity: 1,
                                        padding: 2,
                                        marginLeft: '2%'
                                    }}
                                >
                                    PLANO DE MANUTENÇÃO
                                </Typography>
                                <Divider sx={{ width: '100%', mt: 0 }} />
                            </Grid>
                            {error || success ? (
                                <Snackbar open={true} autoHideDuration={6000}>
                                    <Alert
                                        severity={error ? 'error' : 'success'}
                                        sx={{
                                            width: '100%',
                                            backgroundColor: error ? 'red' : 'green',
                                            color: '#FFF'
                                        }}
                                    >
                                        {error || success}
                                    </Alert>
                                </Snackbar>
                            ) : null}
                            <div style={{ display: loading ? 'none' : 'block' }}>
                                <form noValidate onSubmit={handleSubmit} {...others} ref={formRef}>
                                    <Grid container spacing={matchDownSM ? 1 : 2} sx={{ mb: 3 }}>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                fullWidth
                                                select
                                                label="Ativo"
                                                id="idOperating"
                                                value={operating}
                                                name="idOperating"
                                                onBlur={handleBlur}
                                                options={[]}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Ativo"
                                                        helperText={touched.idOperating && errors.idOperating ? errors.idOperating : ''}
                                                        error={Boolean(touched.idOperating && errors.idOperating)}
                                                    />
                                                )}
                                                disabled={true}
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    {!params.action && (
                                        <div ref={formSectionRef}>
                                            <Card sx={{ mb: 3, border: editingId ? '2px solid #1976d2' : '2px solid #e3f2fd', borderRadius: 2 }}>
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                        <AddCircleOutlineIcon sx={{ color: '#015641', mr: 1, fontSize: 28 }} />
                                                        <Typography variant="h6" sx={{ color: '#015641', fontWeight: 600 }}>
                                                            {editingId ? 'Editar Tarefa do Plano' : 'Adicionar Nova Tarefa ao Plano'}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Grid container spacing={3}>
                                                        {/* Primeira linha - Informações principais */}
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2" sx={{ color: '#666', mb: 2, fontWeight: 500 }}>
                                                                Informações da Tarefa
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}>
                                                            <Autocomplete
                                                                fullWidth
                                                                select
                                                                label="Tarefa *"
                                                                id="task"
                                                                value={currentMaintenance.task}
                                                                name="task"
                                                                onBlur={handleBlur}
                                                                onChange={(e, newValue) =>
                                                                    setCurrentMaintenance({ ...currentMaintenance, task: newValue })
                                                                }
                                                                options={options.task || []}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Tarefa *"
                                                                        helperText={touched.idTask && errors.idTask ? errors.idTask : ''}
                                                                        error={Boolean(touched.idTask && errors.idTask)}
                                                                        size="small"
                                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                    />
                                                                )}
                                                                disabled={isDisabled}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}>
                                                            <Autocomplete
                                                                fullWidth
                                                                select
                                                                label="Equipamento"
                                                                id="equipament"
                                                                value={currentMaintenance.equipament}
                                                                name="equipament"
                                                                onBlur={handleBlur}
                                                                onChange={(e, newValue) =>
                                                                    setCurrentMaintenance({ ...currentMaintenance, equipament: newValue })
                                                                }
                                                                options={options.equipament || []}
                                                                renderOption={(props, option) => (
                                                                    <Box component="li" {...props}>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                wordBreak: 'break-word',
                                                                                lineHeight: 1.3
                                                                            }}
                                                                        >
                                                                            {option.label}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Equipamento"
                                                                        helperText={touched.idEquipament && errors.idEquipament ? errors.idEquipament : ''}
                                                                        error={Boolean(touched.idEquipament && errors.idEquipament)}
                                                                        size="small"
                                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                    />
                                                                )}
                                                                disabled={isDisabled}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4}>
                                                            <Autocomplete
                                                                fullWidth
                                                                select
                                                                label="Frequência *"
                                                                id="frequency"
                                                                value={currentMaintenance.frequency}
                                                                name="frequency"
                                                                onBlur={handleBlur}
                                                                onChange={(e, newValue) =>
                                                                    setCurrentMaintenance({ ...currentMaintenance, frequency: newValue })
                                                                }
                                                                options={options.frequency || []}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Frequência *"
                                                                        helperText={touched.idFrequency && errors.idFrequency ? errors.idFrequency : ''}
                                                                        error={Boolean(touched.idFrequency && errors.idFrequency)}
                                                                        size="small"
                                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                    />
                                                                )}
                                                                disabled={isDisabled}
                                                            />
                                                        </Grid>
                                                        {/* Segunda linha - Configurações de execução */}
                                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ color: '#666', mb: 2, fontWeight: 500 }}>
                                                                Configurações de Execução
                                                            </Typography>
                                                        </Grid>
                                                        
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Autocomplete
                                                                fullWidth
                                                                select
                                                                label="Tipo de OS *"
                                                                id="type_os"
                                                                value={currentMaintenance.type_os}
                                                                name="type_os"
                                                                onBlur={handleBlur}
                                                                onChange={(e, newValue) =>
                                                                    setCurrentMaintenance({ ...currentMaintenance, type_os: newValue })
                                                                }
                                                                options={options.type_os || []}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Tipo de OS *"
                                                                        helperText={touched.idTypeOs && errors.idTypeOs ? errors.idTypeOs : ''}
                                                                        error={Boolean(touched.idTypeOs && errors.idTypeOs)}
                                                                        size="small"
                                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                    />
                                                                )}
                                                                disabled={isDisabled}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <Autocomplete
                                                                fullWidth
                                                                select
                                                                label="Equipe"
                                                                id="team"
                                                                value={currentMaintenance.team}
                                                                name="team"
                                                                onBlur={handleBlur}
                                                                onChange={(e, newValue) =>
                                                                    setCurrentMaintenance({ ...currentMaintenance, team: newValue })
                                                                }
                                                                options={options.team || []}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Equipe"
                                                                        helperText={touched.idTeam && errors.idTeam ? errors.idTeam : ''}
                                                                        error={Boolean(touched.idTeam && errors.idTeam)}
                                                                        size="small"
                                                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                    />
                                                                )}
                                                                disabled={isDisabled || !currentMaintenance.type_os}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <NumberFormat
                                                                fullWidth
                                                                id="outlined-quantidade"
                                                                type="text"
                                                                label="Quantidade *"
                                                                value={currentMaintenance.amount}
                                                                onChange={handleChangeInput}
                                                                name="amount"
                                                                customInput={TextField}
                                                                size="small"
                                                                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                isAllowed={(values) => {
                                                                    const { floatValue, formattedValue } = values;
                                                                    return formattedValue === '' || (floatValue >= 1 && floatValue <= 999);
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={3}>
                                                            <TextField
                                                                fullWidth
                                                                error={Boolean(touched.date && errors.date)}
                                                                id="outlined-date"
                                                                type="date"
                                                                label="Data Próxima Manutenção *"
                                                                onChange={handleChangeInput}
                                                                value={currentMaintenance.data_proxima_manutencao}
                                                                name="data_proxima_manutencao"
                                                                disabled={isDisabled}
                                                                helperText={touched.date && errors.date ? errors.date : ''}
                                                                size="small"
                                                                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fafafa' } }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                            />
                                                        </Grid>
                                                        {/* Terceira linha - Controles */}
                                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Toggle
                                                                        checked={currentMaintenance.active}
                                                                        onChange={(e) =>
                                                                            setCurrentMaintenance({ ...currentMaintenance, active: e.target.checked })
                                                                        }
                                                                        sx={{ mr: 1 }}
                                                                        disabled={isDisabled}
                                                                    />
                                                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                                                        Tarefa Ativa
                                                                    </Typography>
                                                                </Box>
                                                                
                                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                                    {editingId && (
                                                                        <Button
                                                                            variant="outlined"
                                                                            color="secondary"
                                                                            startIcon={<CancelIcon />}
                                                                            onClick={handleCancelEdit}
                                                                            disabled={isDisabled}
                                                                            sx={{ 
                                                                                textTransform: 'none',
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            Cancelar
                                                                        </Button>
                                                                    )}
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="primary"
                                                                            startIcon={editingId ? <SaveIcon /> : <AddIcon />}
                                                                            onClick={editingId ? handleUpdateMaintenance : handleSaveMaintenance}
                                                                            disabled={isDisabled || !currentMaintenance.task || !currentMaintenance.frequency || !currentMaintenance.type_os || !currentMaintenance.amount || !currentMaintenance.data_proxima_manutencao || loading}
                                                                            sx={{ 
                                                                                px: 3, 
                                                                                py: 1,
                                                                                borderRadius: 2,
                                                                                textTransform: 'none',
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            {editingId ? 'Atualizar Tarefa' : 'Adicionar Tarefa'}
                                                                        </Button>
                                                                        {(!currentMaintenance.task || !currentMaintenance.frequency || !currentMaintenance.type_os || !currentMaintenance.amount || !currentMaintenance.data_proxima_manutencao) && (
                                                                            <Typography variant="caption" sx={{ color: '#666', mt: 0.5, fontSize: '0.7rem' }}>
                                                                                Preencha todos os campos obrigatórios
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}
                                    
                                    <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#015641' }}>
                                        Tarefas
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />
                                    {renderMaintenance()}
                                    
                                    <Grid container alignItems="end" justifyContent="end" sx={{ mt: 3 }}>
                                        <Grid item>
                                            <Box sx={{ mt: 2, mr: 3 }}>
                                                <ThemeProvider theme={themeButton}>
                                                    <AnimateButton>
                                                        <Button
                                                            disableElevation
                                                            disabled={isSubmitting}
                                                            fullWidth
                                                            size="large"
                                                            type="button"
                                                            variant="contained"
                                                            color="neutral"
                                                            onClick={() => navigate(-1)}
                                                        >
                                                            Voltar
                                                        </Button>
                                                    </AnimateButton>
                                                </ThemeProvider>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    {errors.submit && (
                                        <Box sx={{ mt: 3 }}>
                                            <FormHelperText error>{errors.submit}</FormHelperText>
                                        </Box>
                                    )}
                                </form>
                            </div>
                        </MainCard>
                    </>
                )}
            </Formik>
        </>
    );
};

export default MaintenancePlan;
